import { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import { searchSymbols } from "../marketData.js";

export const inputCls = "v-mono w-full rounded-lg px-3 py-2 text-sm mt-1";
export const btnGold = "bg-gold-grad text-ink font-semibold rounded-full py-3 text-sm disabled:opacity-40";
export const btnSoft = "bg-card2 rounded-full py-2 px-4 text-xs font-medium disabled:opacity-40";
const GOOD = "var(--sage)", BAD = "var(--rust)";

export function Sheet({ title, onClose, children }) {
  useEffect(() => { const k = (e) => e.key === "Escape" && onClose(); window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [onClose]);
  return (
    <div className="overlay-backdrop fixed inset-0 z-40 flex items-end lg:items-center justify-center" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-label={title} className="bg-card2 rounded-t-3xl lg:rounded-3xl w-full max-w-sm md:max-w-md p-5" style={{ maxHeight: "88vh", overflowY: "auto" }}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="v-display text-lg font-semibold">{title}</h3>
          <button onClick={onClose} aria-label="Close"><X size={18} className="text-ivory-dim" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export const Field = ({ label, hint, children }) => (
  <div className="mb-4"><label className="text-xs text-ivory-dim">{label}</label>{children}{hint && <div className="text-xs text-ivory-dim mt-1 leading-relaxed">{hint}</div>}</div>
);

export function Pills({ value, options, onChange, label, small }) {
  return (
    <div role="radiogroup" aria-label={label} className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}>
      {options.map(([id, text]) => (
        <button key={id} role="radio" aria-checked={value === id} onClick={() => onChange(id)} className={`rounded-lg ${small ? "py-1" : "py-2"} text-xs font-medium`}
          style={{ background: value === id ? "var(--brass)" : "var(--card)", color: value === id ? "var(--ink)" : "var(--ivory-dim)" }}>{text}</button>
      ))}
    </div>
  );
}

export const Delta = ({ v, text, className = "" }) => (
  <span className={`v-mono ${className}`} style={{ color: v == null ? "var(--ivory-dim)" : v >= 0 ? GOOD : BAD }}>{text}</span>
);
export const signed = (n, f) => `${n >= 0 ? "+" : "-"}${f(Math.abs(n))}`;
export const pctText = (n, d = 1) => (n == null || !isFinite(n) ? "—" : `${n >= 0 ? "+" : ""}${n.toFixed(d)}%`);

export const Bar = ({ pct, color }) => (
  <div className="w-full h-1.5 rounded-full bg-card2 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: color }} /></div>
);

export const Notice = ({ tone = "info", children }) => (
  <div className="rounded-xl p-3 mb-4 text-xs leading-relaxed" style={{ background: tone === "warn" ? "rgba(181,68,95,0.12)" : "var(--card2)", color: tone === "warn" ? "var(--ivory)" : "var(--ivory-dim)", border: tone === "warn" ? "1px solid rgba(181,68,95,0.35)" : "none" }}>{children}</div>
);

export const Empty = ({ title, body, children }) => (
  <div className="text-center py-9 text-ivory-dim text-sm"><div className="text-ivory mb-1">{title}</div><div className="mb-3 leading-relaxed">{body}</div>{children}</div>
);

// Time-aware line chart: points = [{ t (ms or ISO date), v }]. Works for net worth snapshots and prices alike.
export function LineChart({ points, fmt, height = 120, color }) {
  const ref = useRef(null);
  const [hover, setHover] = useState(null);
  const pts = (points || []).map((p) => ({ t: typeof p.t === "number" ? p.t : Date.parse(p.t), v: p.v })).filter((p) => isFinite(p.t) && isFinite(p.v)).sort((a, b) => a.t - b.t);
  if (pts.length < 2) return <div className="text-xs text-ivory-dim text-center py-6">Not enough history yet — a line appears once there are two points.</div>;
  const W = 300, H = height, pad = 6;
  const t0 = pts[0].t, t1 = pts[pts.length - 1].t || t0 + 1;
  const lo = Math.min(...pts.map((p) => p.v)), hi = Math.max(...pts.map((p) => p.v)), span = hi - lo || Math.abs(hi) || 1;
  const xy = pts.map((p) => ({ x: ((p.t - t0) / (t1 - t0 || 1)) * W, y: H - pad - ((p.v - lo) / span) * (H - pad * 2) }));
  const up = pts[pts.length - 1].v >= pts[0].v, c = color || (up ? GOOD : BAD);
  const line = xy.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const pick = (clientX) => {
    const r = ref.current?.getBoundingClientRect(); if (!r) return;
    const x = ((clientX - r.left) / r.width) * W; let best = 0, d = Infinity;
    xy.forEach((p, i) => { const dd = Math.abs(p.x - x); if (dd < d) { d = dd; best = i; } });
    setHover(best);
  };
  const h = hover != null ? pts[hover] : null;
  const date = (t) => new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return (
    <div>
      <div className="flex items-center justify-between mb-1 px-0.5 text-xs" style={{ minHeight: 16 }}>
        {h ? <><span className="text-ivory-dim">{date(h.t)}</span><span className="v-mono font-semibold">{fmt(h.v)}</span></> : <><span className="text-ivory-dim">{date(t0)}</span><span className="text-ivory-dim">{date(t1)}</span></>}
      </div>
      <svg ref={ref} viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" role="img" aria-label="Chart"
        onMouseMove={(e) => pick(e.clientX)} onMouseLeave={() => setHover(null)} onTouchStart={(e) => pick(e.touches[0].clientX)} onTouchMove={(e) => pick(e.touches[0].clientX)} onTouchEnd={() => setHover(null)} style={{ touchAction: "none", cursor: "crosshair" }}>
        <polygon points={`0,${H} ${line} ${W},${H}`} fill={c} opacity="0.1" />
        <polyline points={line} fill="none" stroke={c} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        {hover != null && <><line x1={xy[hover].x} x2={xy[hover].x} y1={0} y2={H} style={{ stroke: "var(--grid)" }} /><circle cx={xy[hover].x} cy={xy[hover].y} r="3.5" fill={c} style={{ stroke: "var(--card)" }} strokeWidth="1.5" /></>}
      </svg>
      <div className="flex justify-between text-xs mt-1"><span className="text-ivory-dim">Low <span className="v-mono text-ivory">{fmt(lo)}</span></span><span className="text-ivory-dim">High <span className="v-mono text-ivory">{fmt(hi)}</span></span></div>
    </div>
  );
}

// Ticker box with type-ahead. onPick receives { symbol, id, name, currency, exchange, manual }.
export function SymbolSearch({ assetType, onPick, placeholder, initial = "" }) {
  const [q, setQ] = useState(initial), [hits, setHits] = useState([]), [note, setNote] = useState(""), [busy, setBusy] = useState(false), [open, setOpen] = useState(false);
  useEffect(() => { setQ(initial); setHits([]); }, [assetType]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!q.trim()) { setHits([]); setNote(""); return; }
    setBusy(true);
    let live = true;
    const h = setTimeout(async () => { const r = await searchSymbols(q, assetType); if (!live) return; setHits(r.hits); setNote(r.note || ""); setBusy(false); }, 350);
    return () => { live = false; clearTimeout(h); };
  }, [q, assetType]);
  return (
    <div className="relative">
      <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); onPick({ symbol: e.target.value.trim().toUpperCase(), manual: true }); }} onFocus={() => setOpen(true)} placeholder={placeholder} aria-label="Ticker or name" className="v-mono w-full rounded-lg px-3 py-2 text-sm mt-1" style={{ textTransform: "uppercase" }} />
      {open && q.trim() && (busy || hits.length > 0 || note) && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 right-0 mt-1 rounded-lg z-40 overflow-hidden max-h-56 overflow-y-auto" style={{ background: "var(--card2)", border: "1px solid var(--hair)" }}>
            {busy && <div className="px-3 py-2 text-xs text-ivory-dim">Searching…</div>}
            {!busy && hits.map((r) => (
              <button key={r.id || r.symbol} onClick={() => { onPick({ ...r, manual: false }); setQ(r.symbol); setOpen(false); }} className="w-full text-left px-3 py-2 border-b border-hair last:border-b-0">
                <div className="flex justify-between"><span className="text-xs font-semibold v-mono">{r.symbol}</span><span className="text-xs text-ivory-dim">{[r.exchange, r.currency].filter(Boolean).join(" · ")}</span></div>
                <div className="text-xs text-ivory-dim truncate">{r.name}</div>
              </button>
            ))}
            {!busy && note && <div className="px-3 py-2 text-xs text-ivory-dim">{note}</div>}
          </div>
        </>
      )}
    </div>
  );
}
